// The firm's six risk profiles. ONE definition serves the whole wire.
//
// SEP-243 section 24 keeps THREE risk concepts apart. They share this value
// set, and they never share a field:
//
//   1. The fact-find risk assessment. The ATR questionnaire scores it, and the
//      system owns the result. It is a measurement.
//   2. The INTENDED risk profile (`OnboardingPipelineCase.intendedRiskProfile`).
//      The client states it at the enquiry. It is a preference, and it is
//      evidence of what the client asked for.
//   3. The RECOMMENDED risk profile
//      (`SuitabilityAssessment.riskProfileRecommendation`). The IM concludes it
//      on the Internal Review. It is a professional judgement, and only a
//      person makes it.
//
// **The intended value must NEVER seed the recommended value.** A prefill that
// copies concept 2 into concept 3 makes the suitability judgement for the IM,
// and the file then shows agreement that nobody reached. The Internal Review
// compares the two, and a difference is a fact the reviewer must see.
// `apps/server/src/lib/lint/__tests__/intended-risk-separation-guard.test.ts`
// fails the build when the two names meet on one line.
//
// The values live here so the enquiry intent and the IM recommendation are
// directly comparable: both read this list, and a comparison of the two is a
// string equality. `RISK_PROFILE_TEMPLATES` in `suitability-assessment-compute`
// carries the label and the canonical objective wording for each key.
//
// `@septima/ontology-wealth` cannot import this module, because the ontology
// never depends on the contract. It holds the same list in
// `vendor/septima-ontology-wealth/packages/ontology-wealth/src/risk-profile.ts`, and a server test pins the
// two lists to each other. This is the `AML_VENDOR_RUN_FROM` precedent.

import { z } from 'zod';

/** The six risk profiles, in order from the lowest risk to the highest. */
export const RISK_PROFILE_VALUES = [
    'low',
    'moderately_low',
    'moderate',
    'moderately_high',
    'high',
    'aim_iht',
] as const;

export const riskProfileSchema = z.enum(RISK_PROFILE_VALUES);

/**
 * The key of one risk profile. `RiskProfileKey` in
 * `suitability-assessment-compute` is an alias of this type, so the template
 * table and the schema cannot drift.
 */
export type RiskProfile = z.infer<typeof riskProfileSchema>;

/**
 * The official name of each risk profile. Dowgate uses these names on every
 * screen, document and message that shows a risk category (SEP-318, firm
 * decision 1). `RISK_PROFILE_TEMPLATES` reads its labels from here, so the
 * Suitability Assessment and the client risk rating show the same names.
 */
export function riskProfileLabel(profile: RiskProfile): string {
    switch (profile) {
        case 'low':
            return 'Low Risk';
        case 'moderately_low':
            return 'Moderately Low Risk';
        case 'moderate':
            return 'Moderate Risk';
        case 'moderately_high':
            return 'Moderately High Risk';
        case 'high':
            return 'High Risk';
        case 'aim_iht':
            return 'AIM IHT';
    }
}

/**
 * The official name of a stored risk-profile value. A value that is not one of
 * the six keys comes back as it is, so the screen never hides data that it
 * cannot name. An empty value gives null.
 */
export function riskProfileDisplayLabel(value: string | null | undefined): string | null {
    if (!value) return null;
    const parsed = riskProfileSchema.safeParse(value);
    return parsed.success ? riskProfileLabel(parsed.data) : value;
}

// ---------------------------------------------------------------------------
// The rationale rule (SEP-243 section 24, SEP-257 item 22)
// ---------------------------------------------------------------------------
//
// The firm's instruction: "Where the final recommendation differs materially
// from the preceding indicators, require rationale."
//
// The rule COMPARES, and it never assigns. Nothing here produces a
// recommendation. The IM records the recommendation, and this module states
// whether the file must also carry the reason for it.
//
// # Why the rule lives here as well
//
// `vendor/septima-ontology-wealth/packages/ontology-wealth/src/risk-profile.ts` holds the same function, and
// the final-approval gate reads it there. The ontology never depends on the
// contract, so the wire layer cannot import that copy, and a build guard bars
// `apps/web` from every `@septima/ontology-*` import
// (`apps/web/src/lib/contract-boundary.test.ts`).
//
// SEP-257 item 22 needs the verdict LIVE in the browser, as the IM chooses a
// recommendation, so the verdict must reach the web. This copy is therefore the
// one the wire reads: the pull-through builder
// (`apps/server/src/modules/desk/suitability-prefill.ts`) and the review panel
// both import THIS function, so the screen and the saved verdict are one
// implementation. `apps/server/src/lib/consistency/__tests__/risk-rationale-parity.test.ts`
// runs a case table through both copies and fails the build on a drift. This is
// the `AML_VENDOR_RUN_FROM` precedent, which the six values above already use.
//
// # How the two indicators compare
//
// The INTENDED profile carries the same six values as the recommendation, so
// the comparison is a string equality.
//
// The FACT-FIND BAND is a different scale: the risk questionnaire scores 1 to
// 7. The firm publishes no correspondence between the band and the profile, and
// SEP-243 section 24 forbids the system to invent one, because that is the
// suitability judgement itself. The comparison therefore uses the ORDER that
// each scale already publishes, and nothing else. Each position scales to the
// range 0 to 1, and the difference is MATERIAL when the two positions are more
// than one rung of the profile ladder apart. One rung is a quarter of the
// ladder.
//
// `aim_iht` sits off the ladder. It is a product for inheritance-tax planning,
// and not a rung of the risk scale. A recommendation of `aim_iht` therefore
// always carries a rationale against the fact-find band.

/** The five profiles that hold a position on the risk ladder, lowest first. */
export const RISK_PROFILE_LADDER = [
    'low',
    'moderately_low',
    'moderate',
    'moderately_high',
    'high',
] as const;

export type LadderRiskProfile = (typeof RISK_PROFILE_LADDER)[number];

/** The lowest and the highest band the fact-find questionnaire gives. */
export const FACT_FIND_BAND_MINIMUM = 1;
export const FACT_FIND_BAND_MAXIMUM = 7;

/**
 * The gap that counts as material: one rung of the ladder of five profiles.
 * The comparison adds a small tolerance, so a value that lands exactly one rung
 * away is not material.
 */
const MATERIAL_GAP = 1 / (RISK_PROFILE_LADDER.length - 1);
const TOLERANCE = 1e-9;

/** The position of a profile on the ladder, from 0 to 1. Null when off it. */
export function riskProfilePosition(profile: RiskProfile | null): number | null {
    if (profile === null) return null;
    const index = (RISK_PROFILE_LADDER as ReadonlyArray<string>).indexOf(profile);
    if (index < 0) return null;
    return index / (RISK_PROFILE_LADDER.length - 1);
}

/** The position of a fact-find band on its own scale, from 0 to 1. */
export function factFindBandPosition(band: number | null): number | null {
    if (band === null || !Number.isFinite(band)) return null;
    const clamped = Math.min(FACT_FIND_BAND_MAXIMUM, Math.max(FACT_FIND_BAND_MINIMUM, band));
    return (clamped - FACT_FIND_BAND_MINIMUM) / (FACT_FIND_BAND_MAXIMUM - FACT_FIND_BAND_MINIMUM);
}

/** Why the file must carry a rationale for the risk profile. */
export type RiskRationaleReason = 'differs_from_intent' | 'differs_from_fact_find_band';

export interface RiskRationaleVerdict {
    /** True when the file must carry a reason for the recommendation. */
    readonly required: boolean;
    /** Every reason that fires, so the screen states each one. */
    readonly reasons: ReadonlyArray<RiskRationaleReason>;
}

/**
 * State whether the recommendation needs a written reason.
 *
 * A missing recommendation needs no reason: the IM has reached no conclusion
 * yet. An indicator that the file does not hold raises no reason either.
 */
export function riskRationaleVerdict(args: {
    /** The profile the IM concluded. */
    readonly recommended: RiskProfile | null;
    /** The profile the client asked for at the enquiry. */
    readonly intended: RiskProfile | null;
    /** The band the fact-find risk questionnaire scored. */
    readonly factFindBand: number | null;
}): RiskRationaleVerdict {
    const reasons: RiskRationaleReason[] = [];
    if (args.recommended === null) return { required: false, reasons };

    if (args.intended !== null && args.intended !== args.recommended) {
        reasons.push('differs_from_intent');
    }

    if (args.factFindBand !== null) {
        const bandPosition = factFindBandPosition(args.factFindBand);
        const profilePosition = riskProfilePosition(args.recommended);
        if (bandPosition !== null && profilePosition === null) {
            // The recommendation sits off the ladder, so no position compares.
            reasons.push('differs_from_fact_find_band');
        } else if (
            bandPosition !== null &&
            profilePosition !== null &&
            Math.abs(bandPosition - profilePosition) > MATERIAL_GAP + TOLERANCE
        ) {
            reasons.push('differs_from_fact_find_band');
        }
    }

    return { required: reasons.length > 0, reasons };
}
